<template>
  <BaseDialog
    :visible="visible"
    @update:visible="(val) => emit('update:visible', val)"
    width="full"
    height="auto"
    :dialogClass="dialogClass"
    :data-test-id="testId"
    :fullscreen="true"
    @keydown.escape="$emit('close')"
  >
    <template #header>
      <div
        class="hidden md:flex items-center justify-between mb-[24px] md:mx-[32px] mx-[16px] mt-[20px]"
      >
        <div class="flex gap-[8px] items-center">
          <div
            v-if="headerIcon"
            :class="headerIconClass"
            v-html="headerIcon"
          ></div>
          <h2
            :class="headerClass"
            v-html="headerText"
          ></h2>
        </div>
        <!-- NOSONAR -->
        <div
          class="flex items-center cursor-pointer"
          @click="$emit('close')"
          v-html="closeIcon"
          data-test-id="modal-close-btn"
          :aria-label="$t('accessibilityLabels.close')"
          tabindex="0"
          :autofocus="visible ? true : false"
          @keydown.enter="$emit('close')"
          @keydown.space="$emit('close')"
        ></div>
      </div>

      <!-- Mobile Layout -->
      <div class="md:hidden mb-[24px] md:mx-[32px] mx-[16px]">
        <div class="flex items-center justify-between mb-[16px]">
          <button
            @click="$emit('close')"
            class="cursor-pointer border-none bg-transparent px-0 flex justify-center items-center"
            v-html="backSvg"
            :aria-label="$t('accessibilityLabels.close')"
          ></button>
          <button
            class="cursor-pointer border-none bg-transparent px-0 flex justify-center items-center"
            @click="$emit('close')"
            v-html="cancelSvg"
            data-test-id="modal-close-btn"
            :aria-label="$t('accessibilityLabels.close')"
          ></button>
        </div>
        <div class="flex items-center justify-between">
          <div class="flex gap-[8px] items-center">
            <div
              v-if="headerIcon"
              class="!w-[30px] !h-[30px]"
              :class="headerIconClass"
              v-html="headerIcon"
            ></div>
            <p
              class="!text-[20px]"
              :class="headerClass"
              v-html="headerText"
            ></p>
          </div>
        </div>
      </div>
    </template>
    <div
      class="bg-white px-[16px] md:px-[32px] pb-[32px] rounded-[23.04px] font-just"
      :class="{ '!pb-[10px]': section === 'editPetTrust' }"
    >
      <div
        :class="{
          'mb-[24px]': section !== 'editPetTrust',
          'mb-4': section === 'editPetTrust',
        }"
      >
        <div class="border-[1px] border-solid border-whiteBlue"></div>
      </div>
      <div
        v-if="section === 'addPetTrust' || section === 'editPetTrust'"
        class="gap-6 flex items-start flex-col"
        :class="section === 'editPetTrust' ? 'mb-0' : 'mb-8'"
      >
        <span
          v-if="section !== 'editPetTrust'"
          v-html="$t('petTrustLabels.chooseExistingPet')"
          class="text-[20px] font-normal leading-[100%] text-btnActive tracking-[-0.7%]"
        >
        </span>
        <div class="flex-1 w-full">
          <BaseInput
            v-model="localPetTrustName"
            @update:modelValue="(val) => $emit('update:petTrustName', val)"
            :label="$t('petTrustLabels.petTrustName')"
            type="text"
            name="petTrustName"
            class="sm:col-span-3"
            @blur="validation.localPetTrustName.$touch"
            id="petTrustName"
            inputClasses="!border-lightGrey"
            :invalid="validation.localPetTrustName.$error"
            :errorMessage="validation.localPetTrustName.$errors[0]?.$message"
            :aria-label="$t('accessibilityLabels.search')"
          />
        </div>
      </div>
      <div v-if="section !== 'editPetTrust'">
        <div
          class="flex w-full flex-col md:flex-row md:justify-between items-start md:items-center gap-3"
        >
          <div>
            <p :class="chooseExistingClass" v-html="$t('commonLabels.chooseExistingBeneficiary')"></p>
          </div>
          <div
            class="flex items-center justify-center w-full md:w-auto"
          >
            <BaseInput
              :class="searchInputClass"
              class="!rounded-full !border !border-whiteBlue !bg-[#F7F7F7]"
              iconPosition="right"
              :width="'w-full'"
              height="h-[40px]"
              :icon="searchIcon"
              :height="searchInputHeight"
              :placeholder="$t('commonLabels.search')"
              v-model="search"
              data-test-id="search-input"
              :aria-label="$t('accessibilityLabels.search')"
            />
          </div>
        </div>

        <div :class="itemListClass" class="overflow-x-auto scroll-thin">
          <slot name="item-list">
            <template v-if="props.loading">
              <BaseSkeleton
                v-for="(n, idx) in isMobile ? 1 : 2"
                :key="idx"
                height="13rem"
                width="20rem"
                borderRadius="20px"
                data-test-id="modal-loading"
              ></BaseSkeleton>
            </template>
            <template
              v-else-if="
                section === 'addPetTrust' || section === 'editPetTrust'
              "
            >
              <template v-if="filteredItems.length">
                <div class="flex gap-5 pb-4 overflow-x-auto scroll-thin">
                  <div
                    v-for="item in filteredItems"
                    :key="item.id"
                    class="flex-shrink-0"
                    :style="{ width: isMobile ? '100%' : '320px' }"
                  >
                    <AgentCardComponent
                      v-bind="item"
                      :imageFileId="item?.imageFileId"
                      :selected="
                        isMultiple
                          ? selectedItem.some((i) => i?.id === item.id)
                          : selectedItem?.id === item.id
                      "
                      :section="section"
                      :item="item"
                      :icon="true"
                      :statusIcon="item[cardStatusIconKey]"
                      :showMenu="
                        isMultiple
                          ? !selectedItem ||
                            !selectedItem.length ||
                            !selectedItem.some((i) => i?.id === item.id)
                          : !selectedItem || selectedItem?.id !== item.id
                      "
                      @open-dialog="$emit('select', item)"
                      :showRoleIcons="false"
                      :actionMenuItems="petMenuItemTrust(item)"
                      :isCreateNew="false"
                      :imgId="item.id"
                    >
                      <template #extra-content>
                        <div class="flex items-center justify-between">
                          <span
                            :class="
                              item.cashBequest
                                ? 'text-darkBlue'
                                : 'text-whiteBlue'
                            "
                            class="text-[14.4px] font-normal leading-[100%] tracking-[-0.7%]"
                          >
                            {{ $t('petTrustLabels.cashBequest') }}
                          </span>
                          <span
                            :class="
                              item.cashBequest
                                ? 'text-deepSky bg-whiteBlue'
                                : 'text-whiteBlue bg-softSky'
                            "
                            class="rounded-[7.17px] p-[4.78px] gap-[11.95] text-[14.4px] font-normal leading-[100%] tracking-[-0.7%]"
                            >$ {{ item.cashBequest }}</span
                          >
                        </div>
                      </template>
                    </AgentCardComponent>
                  </div>
                </div>
              </template>
              <template v-else>
                <div
                  class="text-center font-just text-[16px] font-normal leading-[100%] text-btnActive tracking-[-0.7%]"
                  data-test-id="no-results"
                >
                  {{ $t('commonLabels.noDataFound') }}
                </div>
              </template>
            </template>
            <template v-else>
              <template v-if="filteredItems.length">
                <div
                  v-for="item in filteredItems"
                  :key="item.id"
                  :data-test-id="'beneficiary-item'"
                  :class="[
                    {
                      disabled: item.disabled,
                      'cursor-not-allowed': item.disabled,
                    },
                  ]"
                >
                  <AgentCardComponent
                    v-bind="item"
                    :section="section"
                    :imageFileId="item?.imageFileId"
                    :selected="
                      isMultiple
                        ? selectedItem.some((i) => i?.id === item.id)
                        : selectedItem?.id === item.id
                    "
                    :item="item"
                    :icon="true"
                    :imgId="item.id"
                    :statusIcon="item[cardStatusIconKey]"
                    :showMenu="item[cardShowMenuKey]"
                    @open-dialog="$emit('select', item)"
                    :showRoleIcons="section !== 'addPet'"
                    :actionMenuItems="[]"
                    :isCreateNew="false"
                    ><template #extra-content v-if="section === 'addPet'">
                      <div class="flex items-center justify-between mt-1">
                        <span
                          class="text-[14.4px] font-normal leading-[100%] text-whiteBlue tracking-[-0.7%]"
                        >
                          {{ $t('petTrustLabels.cashBequest') }}
                        </span>
                        <span
                          class="text-whiteBlue bg-softSky rounded-[7.17px] p-[4.78px] gap-[11.95] text-[14.4px] font-normal leading-[100%] tracking-[-0.7%]"
                          >$ 0</span
                        >
                      </div>
                    </template></AgentCardComponent
                  >
                </div>
              </template>
              <template v-else>
                <div
                  class="text-center font-just text-[16px] font-normal leading-[100%] text-btnActive tracking-[-0.7%]"
                  data-test-id="no-results"
                >
                  {{ $t('commonLabels.noDataFound') }}
                </div>
              </template>
            </template>
          </slot>
        </div>

        <div v-if="showCreateNew" :class="createNewSectionClass">
          <p :class="createNewLabelClass" v-html="createNewLabel"></p>
        </div>

        <div v-if="showCreateNew">
          <slot name="create-new">
            <AgentCardComponent
              v-bind="createNewItem"
              :isCreateNew="true"
              :isSubtitleNeeded="false"
              @open-dialog="$emit('create')"
              :imgId="createNewItem.id"
              data-test-id="create-new-item"
            >
              <template #extra-content>
                <div>
                  <span :class="createNewExtraClass">
                    {{ $t('commonLabels.createNewExtra') }}
                  </span>
                </div>
              </template>
            </AgentCardComponent>
          </slot>
        </div>
      </div>
    </div>
    <template #footer>
      <div :class="confirmBtnSectionClass">
        <div v-if="error">
          <span
            class="text-base my-2 !font-just text-error flex justify-center"
            data-test-id="errorMessage"
          >
            {{ error }}
          </span>
        </div>
        <BaseButton
          :class="confirmBtnClass"
          :disabled="
            (section !== 'editPetTrust' && !selectedItem) ||
            (section === 'addPetTrust' || section === 'editPetTrust'
              ? !localPetTrustName
              : false)
          "
          @click="$emit('confirm')"
          :loading="loading"
          data-test-id="confirm-selection-btn"
        >
          <div class="flex items-center justify-center">
            <div class="flex items-center gap-[8px]">
              <div>
                <p>{{ confirmBtnText }}</p>
              </div>
            </div>
          </div>
        </BaseButton>
      </div>
    </template>
  </BaseDialog>
</template>

<script setup>
// In SelectPrincipalBackupPersonModal.vue - Update the script section:

import { ref, computed, watch, reactive } from "vue";
import { useI18n } from "vue-i18n";
import BaseDialog from "@/components/Base/BaseDialog.vue";
import BaseInput from "@/components/Base/BaseInput.vue";
import BaseButton from "@/components/Base/BaseButton.vue";
import AgentCardComponent from "@/views/EstatePlan/CommonComponent/AgentCardComponent.vue";
import { deleteSvg, refreshSvg, backArrowSvg, closeSvg } from "@/utils/commonSvg";
import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";

const { t } = useI18n();

const emit = defineEmits([
  "update:petTrustName",
  "select",
  "confirm",
  "close",
  "create",
  "editPet",
  "removePet",
]);

const props = defineProps({
  error: { type: String, default: "" },
  testId: { type: String, default: "" },
  petTrustName: String,
  loading: Boolean,
  section: { type: String, require: true },
  visible: Boolean,
  // ... rest of your props
  headerIcon: String,
  headerIconClass: String,
  headerText: String,
  headerClass: String,
  closeIcon: String,
  chooseExistingLabel: String,
  chooseExistingHighlight: String,
  chooseExistingClass: String,
  chooseExistingHighlightClass: String,
  searchPlaceholder: String,
  searchIcon: String,
  searchInputClass: String,
  searchInputHeight: String,
  items: {
    type: Array,
    default: () => [],
  },
  itemKey: {
    type: String,
    default: "id",
  },
  itemListClass: String,
  isMultiple: { type: Boolean, default: false },
  selectedItem: {},
  cardTitleKey: {
    type: String,
    default: "title",
  },
  cardSubtitleKey: {
    type: String,
    default: "subtitle",
  },
  cardTagKey: {
    type: String,
    default: "tag",
  },
  cardBadgeIconKey: {
    type: String,
    default: "badgeIcon",
  },
  cardIconKey: {
    type: String,
    default: "Icon",
  },
  cardStatusIconKey: {
    type: String,
    default: "statusIcon",
  },
  cardShowMenuKey: {
    type: String,
    default: "showMenu",
  },
  cardSelectedLabel: String,
  cardTapToSelectLabel: String,
  cardRolesLabel: String,
  showCreateNew: Boolean,
  createNewSectionClass: String,
  createNewLabel: String,
  createNewLabelClass: String,
  youCanLabel: String,
  youCanLabelClass: String,
  createNewHighlight: String,
  createNewHighlightClass: String,
  createNewExtra: String,
  createNewExtraClass: String,
  createNewItem: Object,
  confirmBtnText: String,
  confirmBtnClass: String,
  confirmBtnSectionClass: String,
  dialogClass: String,
  searchFields: {
    type: Array,
    default: () => ["name"],
  },
});

const fullscreen = ref(window.innerWidth <= 640);
const isMobile = computed(() => window.innerWidth < 640);
const search = ref("");
const backSvg = computed(() =>
  backArrowSvg({ width: "24px", height: "24px", color: "#818181" })
);
const cancelSvg = computed(() => closeSvg({ width: "24px", height: "24px" }));

const filteredItems = computed(() => {
  if (!search.value) return props.items;
  return props.items.filter((item) => {
    return props.searchFields.some((field) => {
      return (
        item[field] &&
        item[field].toLowerCase().includes(search.value.toLowerCase())
      );
    });
  });
});

// Use ref for localPetTrustName
const localPetTrustName = ref(props?.petTrustName || "");

// Watch for prop changes
watch(
  () => props.petTrustName,
  (newVal) => {
    localPetTrustName.value = newVal || "";
  }
);

// Watch when modal is closed or opened to reset the field
watch(
  () => props.visible,
  (newVal) => {
    if (newVal && props.section === "addPetTrust") {
      // Reset when opening for new pet trust creation
      localPetTrustName.value = "";
      // Also reset validation
      validation.value.$reset();
    }
  }
);

// Update parent when local value changes
watch(localPetTrustName, (newVal) => {
  emit("update:petTrustName", newVal);
});

const petMenuItemTrust = (item) => {
  let actions = [
    {
      label: t('commonLabels.changeBeneficiary'),
      icon: refreshSvg({ color: "#05A8F4" }),
      class: "",
      onClick: (close) => {
        emit("editPet", item);
        close();
      },
    },
    {
      label: t('commonLabels.remove'),
      icon: deleteSvg(),
      class: "!text-alert",
      confirm: true,
      onConfirm: async function (close) {
        emit("removePet", item);
        close();
      },
    },
  ];
  return actions;
};

// Reactive state for validation
const state = reactive({
  localPetTrustName: localPetTrustName.value,
});

// Watch localPetTrustName changes and update state
watch(localPetTrustName, (newVal) => {
  state.localPetTrustName = newVal;
});

const rules = {
  localPetTrustName: {
    required: helpers.withMessage(t('petTrustLabels.enterPetTrustName'), required),
  },
};

const validation = useVuelidate(rules, state);
</script>